import { useEffect, useState } from "react";
import { Alert, Modal, Progress, Radio, Space, Tag } from "antd";
import { toast } from "react-toastify";
import {
  fetchData,
  postResponseData,
  get409Detail,
  is409WithCode,
} from "../../api/apiService";
import { CLIENTS } from "../../api/endpointMap";
import UserAssignmentSelect, {
  toApiValue,
} from "../UserAssignmentSelect";

// Deactivation modal (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.4) — opened by the
// Team page when a Disable / Delete attempt returns 409 USER_HAS_ASSIGNMENTS.
//
// Flow per spec:
//   1. Show counts per portal from the 409 `detail.counts` block.
//   2. Admin picks: "Reassign all to <user>" OR "Mark all as Shared".
//   3. For each portal with count > 0:
//        a. Paginate `GET /clients/{portal}?assigned_user_id=<userId>` to
//           collect ALL client ids (this is N+1 round-trips but it's a
//           one-off admin action — small spinner is fine).
//        b. POST /clients/{portal}/bulk-assign with collected ids + target.
//   4. Retry the original disable/delete action.
//   5. If retry now succeeds → close + bubble onSuccess (caller refreshes).
//   6. If retry returns 409 LAST_ADMIN_PROTECTED → swap to plain error
//      ("Promote another user to admin first") — caller still keeps modal
//      closed; we render the message inline via a state flag.
//
// Props:
//   open       — bool
//   user       — {id, full_name, email} of the target user being deactivated
//   counts     — {income_tax, gst, tds} from the 409 detail
//   actionLabel — "Disable" | "Delete" (cosmetic; goes in the title)
//   retryAction — async () => any  (caller-supplied: re-runs the original
//                disable/delete request after the bulk-assigns finish)
//   onCancel   — () => void
//   onSuccess  — () => void  (caller refreshes its list)

// BE counts use snake_case keys; URL segments are kebab-case.
const PORTAL_KEY_TO_SEGMENT = {
  income_tax: "income-tax",
  gst: "gst",
  tds: "tds",
};

const PORTAL_LABELS = {
  income_tax: "IT",
  gst: "GST",
  tds: "TDS",
};

// Cap on how many client ids we fetch per page during the id-collection
// pass. Matches the BE's typical max page size + keeps each round-trip
// reasonable; the modal loops pages until the response carries fewer
// items than we asked for.
const PAGE_SIZE = 100;

// Defensive cap on total pages we'll walk before giving up. A 5000-client
// org reassignment isn't going to happen via this modal — but if it does,
// fail loud rather than wedge the UI.
const MAX_PAGES = 50;

// Mode constants for the radio choice.
const MODE_REASSIGN = "reassign";
const MODE_SHARED = "shared";

const collectClientIds = async (portal, userId, onProgress) => {
  const collected = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const data = await fetchData(`/api/v1/clients/${portal}`, {
      page,
      page_size: PAGE_SIZE,
      assigned_user_id: userId,
    });
    const items = data?.items || [];
    for (const item of items) {
      const id = item?.id || item?.name;
      if (id) collected.push(id);
    }
    onProgress?.(portal, collected.length);
    if (items.length < PAGE_SIZE) break;
  }
  return collected;
};

const UserDeactivationModal = ({
  open,
  user,
  counts,
  actionLabel = "Disable",
  retryAction,
  onCancel,
  onSuccess,
}) => {
  const [mode, setMode] = useState(MODE_SHARED);
  // Picker value when mode === MODE_REASSIGN — an array of target
  // user-ids ([] = Shared). An empty array is a legitimate fallback if
  // the admin selects "reassign" but leaves the picker untouched —
  // toApiValue() keeps it [], which is identical to MODE_SHARED. No harm.
  const [pickerValue, setPickerValue] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Per-portal progress message shown beneath the Progress bar during
  // the bulk-reassign loop ("Reassigning IT clients (12 collected)…").
  const [progressLabel, setProgressLabel] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);
  // Surfaces a 409 LAST_ADMIN_PROTECTED that arrives on the RETRY (after
  // bulk-assigns have already succeeded). The message comes from the BE
  // detail body. Distinct from generic errors which go to a toast.
  const [retryBlockedMessage, setRetryBlockedMessage] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode(MODE_SHARED);
    setPickerValue([]);
    setSubmitting(false);
    setProgressLabel(null);
    setProgressPercent(0);
    setRetryBlockedMessage(null);
  }, [open]);

  if (!user) return null;

  const safeCounts = counts || {};
  const totalAssignments = Object.values(safeCounts).reduce(
    (sum, n) => sum + (typeof n === "number" ? n : 0),
    0
  );

  // Portals we actually need to walk — only those with counts > 0. Saves
  // empty round-trips and keeps progress messaging honest.
  const portalsToProcess = Object.entries(safeCounts)
    .filter(([, n]) => typeof n === "number" && n > 0)
    .map(([key]) => key);

  const handleContinue = async () => {
    setSubmitting(true);
    setRetryBlockedMessage(null);
    try {
      // Resolve target. MODE_SHARED → [] (Shared pool); MODE_REASSIGN
      // → the picker's array of user-ids (or [] if admin left it empty,
      // which is functionally identical to Shared).
      const targetUserIds =
        mode === MODE_SHARED ? [] : toApiValue(pickerValue);

      // Bulk-reassign loop. We process portals sequentially so the
      // progress UI is meaningful and so a partial failure (e.g. BE
      // 500 on one portal's bulk endpoint) doesn't leave us in an
      // ambiguous state — the admin can re-trigger and the previously-
      // succeeded portals will simply have count=0 next time around.
      for (let i = 0; i < portalsToProcess.length; i += 1) {
        const key = portalsToProcess[i];
        const portal = PORTAL_KEY_TO_SEGMENT[key];
        const label = PORTAL_LABELS[key] || key;
        setProgressLabel(`Collecting ${label} clients…`);
        setProgressPercent(Math.round((i / (portalsToProcess.length + 1)) * 100));

        const ids = await collectClientIds(portal, user.id, (_, n) =>
          setProgressLabel(`Collecting ${label} clients (${n})…`)
        );

        if (ids.length > 0) {
          setProgressLabel(`Reassigning ${ids.length} ${label} clients…`);
          await postResponseData(CLIENTS.BULK_ASSIGN(portal), {
            client_ids: ids,
            user_ids: targetUserIds,
          });
        }
      }

      // All portals reassigned. Retry the original disable/delete.
      setProgressLabel("Retrying…");
      setProgressPercent(95);
      await retryAction();
      setProgressPercent(100);
      toast.success(
        `${actionLabel.toLowerCase()} complete; ${totalAssignments} client${
          totalAssignments === 1 ? "" : "s"
        } reassigned.`
      );
      onSuccess?.();
    } catch (err) {
      // LAST_ADMIN_PROTECTED on retry: bulk-assigns succeeded but the
      // user can't be disabled because they're the last enabled
      // tenant_admin. Surface inline so the admin knows the reassign
      // worked and what's left to do (promote someone else first).
      if (is409WithCode(err, "LAST_ADMIN_PROTECTED")) {
        const detail = get409Detail(err);
        setRetryBlockedMessage(
          detail?.message ||
            "Cannot disable the last remaining admin - promote another user first."
        );
        return;
      }
      const msg =
        err?.detail || err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof msg === "string" ? msg : `Failed to ${actionLabel.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const userName = user.full_name || user.email || user.id;

  return (
    <Modal
      open={open}
      title={`Cannot ${actionLabel.toLowerCase()} ${userName}`}
      onCancel={onCancel}
      onOk={handleContinue}
      okText={
        submitting
          ? "Reassigning…"
          : mode === MODE_SHARED
          ? "Mark all Shared & continue"
          : "Reassign all & continue"
      }
      okButtonProps={{
        loading: submitting,
        disabled: submitting,
      }}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={!submitting}
      closable={!submitting}
      width={560}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        className="mb-3"
        message={`User has ${totalAssignments} client${
          totalAssignments === 1 ? "" : "s"
        } assigned`}
        description="Reassign or unassign before continuing. We'll handle this in one click - pick what to do with the assignments below."
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {Object.entries(safeCounts).map(([key, n]) => (
          <Tag key={key} color={n > 0 ? "blue" : "default"}>
            {PORTAL_LABELS[key] || key}: {n}
          </Tag>
        ))}
      </div>

      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        disabled={submitting}
        className="w-full"
      >
        <Space direction="vertical" className="w-full">
          <Radio value={MODE_SHARED}>Mark all as Shared (no one assigned)</Radio>
          <Radio value={MODE_REASSIGN}>Reassign all to:</Radio>
        </Space>
      </Radio.Group>

      {mode === MODE_REASSIGN && (
        <div className="mt-2 ml-6">
          <UserAssignmentSelect
            value={pickerValue}
            onChange={setPickerValue}
            // forceShow + hideWhenSingleUser=false: in a strict single-
            // user org we'd never reach this modal (no one to disable
            // who has assignments, since they'd be the lone admin). But
            // belt-and-braces — the picker should always render here.
            forceShow
            hideWhenSingleUser={false}
            disabled={submitting}
          />
        </div>
      )}

      {progressLabel && (
        <div className="mt-4">
          <Progress
            percent={progressPercent}
            status={retryBlockedMessage ? "exception" : "active"}
            size="small"
          />
          <div className="text-xs text-gray-600 mt-1">{progressLabel}</div>
        </div>
      )}

      {retryBlockedMessage && (
        <Alert
          type="error"
          showIcon
          className="mt-3"
          message="Cannot complete"
          description={retryBlockedMessage}
        />
      )}
    </Modal>
  );
};

export default UserDeactivationModal;
